import {
  apiRequestResponseMsg,
  defaultSsrSettings,
  defaultSsrSettingsWixMini,
  defaultWebsiteId,
  tabNames,
  websiteFlagKeys,
} from '@va/constants';
import {
  CREATE_SSR_SETTINGS,
  createSsrSettings,
  createSsrSettingsFailed,
  createSsrSettingsSucceeded,
  GET_SSR_SETTINGS,
  GET_VIDEO_TRACKING_CODE_REQUEST,
  getSsrSettingsFailed,
  getSsrSettingsSucceeded,
  getVideoTrackingCodeFailed,
  getVideoTrackingCodeSucceeded,
  PROMPT_SSR_FEATURE,
  promptSsrFeatureFailed,
  promptSsrFeatureSucceeded,
  requestWebsite,
  UPDATE_SSR_SETTINGS,
  updateSsrSettingsFailed,
  updateSsrSettingsSucceeded,
  updateWebsiteFlags,
  upsertSsrSettings,
} from '@va/dashboard/actions/api';

import Api from '@va/dashboard/api-client/index';
import { getApiRequest } from '@va/dashboard/selectors/api';
import { getInstanceId } from '@va/dashboard/selectors/app';
import { getActiveTab } from '@va/dashboard/selectors/ui';
import { isValidWebsiteId, isWixMiniApp } from '@va/util/helpers';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

export function* watchers() {
  yield all([
    takeLatest(PROMPT_SSR_FEATURE, promptSsrFeatureSaga),
    takeLatest(GET_VIDEO_TRACKING_CODE_REQUEST, getVideoTrackingCodeSaga),
    takeLatest(CREATE_SSR_SETTINGS, createSsrSettingsSaga),
    takeLatest(GET_SSR_SETTINGS, getSsrSettingsSaga),
    takeLatest(UPDATE_SSR_SETTINGS, updateSsrSettingsSaga),
    takeLatest(upsertSsrSettings.type, upsertSsrSettingsSaga),
  ]);
}

/** @deprecated */
function* promptSsrFeatureSaga(action) {
  try {
    if (action.value === false) {
      const activeTab = yield select(getActiveTab);
      const flagName = activeTab === tabNames.heatmaps ? websiteFlagKeys.ACCEPTED_HEATMAP : 'acceptedSsr';
      yield put(updateWebsiteFlags({ [flagName]: action.value })); // Flag used to identify websites that rejected ssr feature after beta release. True flag is set from api when createSsrSettings or activateHeatmaps
    }
    yield put(promptSsrFeatureSucceeded());
  } catch (error) {
    yield put(promptSsrFeatureFailed(error));
  }
}

function* getVideoTrackingCodeSaga() {
  try {
    const instanceId = yield select(getInstanceId);
    const coreWebsiteId = yield select(getInstanceId);
    const websiteId = instanceId !== defaultWebsiteId ? instanceId : coreWebsiteId;
    const response = yield call(Api.getVideoTrackingCode, websiteId);
    yield put(getVideoTrackingCodeSucceeded(response));
  } catch (error) {
    yield put(getVideoTrackingCodeFailed(error));
  }
}

function* createSsrSettingsSaga(action) {
  try {
    const websiteId = yield select(getInstanceId);
    const response = yield call(Api.createSsrSettings, websiteId, action.data);
    yield put(createSsrSettingsSucceeded(response));
    yield put(getSsrSettingsSucceeded(response)); // because the create returns the new ssr settings, so it's like you get it again
    yield put(requestWebsite());
  } catch (error) {
    yield put(createSsrSettingsFailed(error));
    yield put(getSsrSettingsFailed(error));
  }
}

function* getSsrSettingsSaga() {
  try {
    const websiteId = yield select(getInstanceId);
    if (!isValidWebsiteId(websiteId)) throw new Error('No active website');
    const response = yield call(Api.getSsrSettings, websiteId);
    yield put(getSsrSettingsSucceeded(response));
  } catch (error) {
    yield put(getSsrSettingsFailed(error));
  }
}

function* upsertSsrSettingsSaga() {
  try {
    const websiteId = yield select(getInstanceId);
    if (!isValidWebsiteId(websiteId)) throw new Error('No active website');
    const response = yield call(Api.getSsrSettings, websiteId);
    yield put(getSsrSettingsSucceeded(response));
  } catch (error) {
    if (error?.message === apiRequestResponseMsg.NOT_FOUND_SSR_SETTINGS) {
      yield put(createSsrSettings(isWixMiniApp() ? defaultSsrSettingsWixMini : defaultSsrSettings));
    } else {
      yield put(getSsrSettingsFailed(error));
    }
  }
}

function* updateSsrSettingsSaga(action) {
  try {
    const websiteId = yield select(getInstanceId);
    const ssrSettings = yield select(getApiRequest, 'getSsrSettings');
    const response = yield call(Api.updateSsrSettings, websiteId, ssrSettings.key, action.data);
    yield put(updateSsrSettingsSucceeded(response));
    yield put(getSsrSettingsSucceeded(response)); // because the update returns the new ssr settings, so it's like you get it again
  } catch (error) {
    yield put(updateSsrSettingsFailed(error));
  }
}
